import React, { useEffect, useState } from "react";
import {
  BarChart3, TrendingUp, Users, Eye, Target, Loader2, Lightbulb, Clock,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import NoProject from "@/components/NoProject";

const StatCard = ({ icon: Icon, label, value, suffix, color }) => (
  <div className="glass rounded-2xl p-5 border border-border card-hover">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}><Icon className="w-5 h-5" /></div>
    <div className="text-2xl font-extrabold">{value}{suffix}</div>
    <div className="text-xs text-muted-foreground mt-1">{label}</div>
  </div>
);

export default function Analytics() {
  const { activeProject } = useProjects();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeProject) { setLoading(false); return; }
    setLoading(true);
    api.get(`/analytics?project_id=${activeProject.id}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [activeProject]);

  if (!activeProject) return <NoProject message="اختر مشروعاً لعرض تقارير أدائه." />;
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!data) return null;

  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-extrabold font-heading">التقارير والتحليلات</h1>
        <p className="text-muted-foreground">أداء حملات «{activeProject.name}» في السوق السعودي</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="منشورات منشورة" value={s.published_posts.toLocaleString("ar")} color="bg-sky-500/15 text-sky-400" />
        <StatCard icon={BarChart3} label="منشورات مقيَّمة" value={s.measured_posts.toLocaleString("ar")} color="bg-primary/15 text-primary" />
        <StatCard icon={TrendingUp} label="إجمالي التفاعلات" value={s.total_interactions.toLocaleString("ar")} color="bg-accent/15 text-accent" />
        <StatCard icon={Target} label="متوسط التفاعل/منشور" value={s.avg_interactions.toLocaleString("ar")} color="bg-fuchsia-500/15 text-fuchsia-400" />
      </div>

      {!data.has_data ? (
        <div className="glass rounded-2xl p-10 border border-border text-center" data-testid="analytics-empty">
          <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-7 h-7 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-bold mb-2">لا توجد بيانات أداء بعد</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            لم يبدأ النشر أو لم تُدخل نتائج بعد. انشر منشوراتك من صفحة الحملات ثم أدخل نتائج كل منشور
            (إعجابات / تعليقات / مشاركات)، وستظهر تحليلاتك الحقيقية هنا تلقائياً.
          </p>
        </div>
      ) : (
      <div className="glass rounded-2xl p-6 border border-border">
        <div className="flex items-center gap-2 mb-5"><BarChart3 className="w-5 h-5 text-primary" /><h2 className="text-lg font-bold">تفاعل المنشورات المقيَّمة</h2></div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data.trend} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="reachG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#734F96" stopOpacity={0.5} /><stop offset="95%" stopColor="#734F96" stopOpacity={0} /></linearGradient>
              <linearGradient id="engG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#D89134" stopOpacity={0.5} /><stop offset="95%" stopColor="#D89134" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke="#94A3B8" fontSize={12} reversed />
            <YAxis stroke="#94A3B8" fontSize={12} />
            <Tooltip contentStyle={{ background: "#131C27", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, direction: "rtl" }} />
            <Area type="monotone" dataKey="interactions" name="إجمالي التفاعلات" stroke="#734F96" fill="url(#reachG)" strokeWidth={2} />
            <Area type="monotone" dataKey="likes" name="الإعجابات" stroke="#D89134" fill="url(#engG)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-2 mb-4"><Clock className="w-5 h-5 text-primary" /><h2 className="text-lg font-bold">أفضل أوقات النشر</h2></div>
          <div className="space-y-3">
            {Object.entries(data.best_times).map(([platform, times]) => (
              <div key={platform} className="flex items-center justify-between bg-secondary rounded-xl p-3">
                <span className="font-semibold text-sm">{platform}</span>
                <div className="flex gap-2">{times.map((t, i) => <span key={i} className="text-xs font-mono bg-primary/15 text-primary px-2 py-1 rounded-lg">{t}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="glass rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-2 mb-4"><Lightbulb className="w-5 h-5 text-accent" /><h2 className="text-lg font-bold">نصائح لتحسين التفاعل</h2></div>
          <ul className="space-y-3">
            {data.tips.map((tip, i) => (
              <li key={i} className="flex gap-3 text-sm"><span className="w-6 h-6 rounded-lg bg-accent/15 text-accent flex items-center justify-center shrink-0 text-xs font-bold">{i + 1}</span>{tip}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
